/**
 * 语音配置 Hook
 * v6.0 新增：加载 ASR/TTS 提供商和音色列表
 * 
 * 用于 Entry 节点（ASR 配置）和 Output 节点（TTS 配置）
 */

import { useState, useEffect, useCallback } from 'react'
import { 
  VoiceConfigApiService, 
  type ASRProvider, 
  type TTSProvider, 
  type TTSVoice,
  type AudioFormat 
} from '@/modules/agent/services/voice-config-api'

interface VoiceConfigState {
  // ASR 配置
  asrProviders: ASRProvider[]
  defaultAsrProvider: string
  
  // TTS 配置
  ttsProviders: TTSProvider[]
  defaultTtsProvider: string
  
  // 音色配置（按提供商分组）
  voices: {
    cosyvoice: TTSVoice[]
    volcano: TTSVoice[]
    elevenlabs: TTSVoice[]
    custom: TTSVoice[]
  }
  
  // 音频格式
  audioFormats: AudioFormat[]
  
  // 状态
  isLoading: boolean
  error: string | null
}

/**
 * 获取语音配置
 * @returns { asrProviders, ttsProviders, voices, audioFormats, isLoading, error, refetch }
 */
export function useVoiceConfig() {
  const [state, setState] = useState<VoiceConfigState>({
    asrProviders: [],
    defaultAsrProvider: '',
    ttsProviders: [],
    defaultTtsProvider: '',
    voices: {
      cosyvoice: [],
      volcano: [],
      elevenlabs: [],
      custom: [],
    },
    audioFormats: [],
    isLoading: false,
    error: null,
  })

  const loadVoiceConfig = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))
    
    try {
      // 并行加载所有配置
      const [asrRes, ttsRes, voicesRes] = await Promise.all([
        VoiceConfigApiService.getASRProviders(),
        VoiceConfigApiService.getTTSProviders(),
        VoiceConfigApiService.getTTSVoices(),
      ])

      // 安全访问，避免 undefined 导致崩溃
      setState({
        asrProviders: asrRes?.data?.providers || [],
        defaultAsrProvider: asrRes?.data?.default_provider || '',
        ttsProviders: ttsRes?.data?.providers || [],
        defaultTtsProvider: ttsRes?.data?.default_provider || '',
        voices: voicesRes?.data?.voices || { cosyvoice: [], volcano: [], elevenlabs: [], custom: [] },
        audioFormats: voicesRes?.data?.audio_formats || [],
        isLoading: false,
        error: null,
      })
    } catch (err) {
      console.error('Failed to load voice config:', err)
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : '加载语音配置失败',
      }))
    }
  }, [])

  // 首次加载
  useEffect(() => {
    loadVoiceConfig()
  }, [loadVoiceConfig])

  // 获取指定提供商的音色列表
  const getVoicesByProvider = useCallback((provider: string): TTSVoice[] => {
    switch (provider) {
      case 'cosyvoice':
        return state.voices.cosyvoice
      case 'volcano':
        return state.voices.volcano
      case 'elevenlabs':
        return state.voices.elevenlabs
      case 'custom':
        return state.voices.custom
      default:
        return []
    }
  }, [state.voices])

  // 获取默认音色 ID
  const getDefaultVoiceId = useCallback((provider: string): string => {
    const voices = getVoicesByProvider(provider)
    const defaultVoice = voices.find(v => v.default)
    return defaultVoice?.id || voices[0]?.id || ''
  }, [getVoicesByProvider])

  // 获取默认音频格式
  const getDefaultAudioFormat = useCallback((): string => {
    const defaultFormat = state.audioFormats.find(f => f.default)
    return defaultFormat?.id || 'mp3'
  }, [state.audioFormats])

  return {
    ...state,
    getVoicesByProvider,
    getDefaultVoiceId,
    getDefaultAudioFormat,
    refetch: loadVoiceConfig,
  }
}

